import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  ChartBarIcon,
  NoSymbolIcon,
  PauseCircleIcon,
  PencilSquareIcon,
  PlayCircleIcon,
  PlusIcon,
} from '@heroicons/react/24/outline';
import { PageHeader } from '@/components/layout';
import {
  Button,
  Input,
  Select,
  Card,
  CardHeader,
  Badge,
  Pagination,
  Modal,
  ModalFooter,
  ErrorSummary,
  CompactFilterBar,
  Table,
} from '@/components/common';
import type { Column, SortDirection } from '@/components/common/Table';
import { AddressFields } from '@/components/forms/AddressFields';
import {
  BROKER_STATUS_ACTIONS,
  BROKER_STATUS_EFFECTS,
  useBrokers,
  useCreateBroker,
  useSetBrokerStatus,
  type BrokerSettableStatus,
} from '@/hooks/useBrokers';
import { useDebouncedSearch } from '@/hooks/useDebouncedSearch';
import { useFilterParams } from '@/hooks/useFilterParams';
import { useAuthStore } from '@/store';
import { formatDate } from '@/utils';
import { applyServerErrors } from '@/utils/formErrors';
import { phoneError } from '@/utils/validation';
import type { Broker, BrokerFilters, BrokerCreateRequest } from '@/types';
import { licenseExpiryState } from './licenseExpiryState';

/**
 * One-time admin credentials returned by the broker-create endpoint. The
 * service is typed as returning a `Broker`, but the create response also
 * carries these write-once fields, so we narrow locally.
 */
interface BrokerAdminCredentials {
  email: string;
  password: string;
  must_change_password?: boolean;
  message?: string;
}

/**
 * The broker admin's own User row, resolved by the API from the broker's
 * `BrokerUser(role=ADMIN)` membership. `email`/`phone` on the broker are read
 * through to this same record, which is why the Contact column and Country
 * Admin > Users now agree (F5). Narrowed locally rather than widened on the
 * shared `Broker` type, matching the `admin_credentials` narrowing above.
 */
interface BrokerAdminIdentity {
  admin_id?: string | null;
  admin_name?: string | null;
  admin_phone?: string | null;
}

const adminOf = (broker: Broker): BrokerAdminIdentity => broker as Broker & BrokerAdminIdentity;

const LICENSE_TYPE_OPTIONS = [
  { value: 'direct', label: 'Direct Broker' },
  { value: 'composite', label: 'Composite Broker' },
  { value: 'reinsurance', label: 'Reinsurance Broker' },
];

/** Every field Add Broker owns, empty. Kept beside the modal's reset effect so
 *  a reopened modal cannot show the last attempt's values. */
const EMPTY_NEW_BROKER: BrokerCreateRequest = {
  name: '',
  email: '',
  phone: '',
  website: '',
  license_number: '',
  license_type: 'direct',
  license_valid_from: '',
  license_valid_until: '',
  pan_number: '',
  gst_number: '',
  cin_number: '',
  address_line1: '',
  address_line2: '',
  city: '',
  state: '',
  pincode: '',
  admin_password: '',
};

const statusOptions = [
  { value: '', label: 'All Status' },
  { value: 'active', label: 'Active' },
  { value: 'pending', label: 'Pending' },
  { value: 'suspended', label: 'Suspended' },
  { value: 'blocked', label: 'Blocked' },
];

/**
 * Q-01 -- the filter state this page mirrors into the query string.
 *
 * Module-level, because `useFilterParams` reads it once to learn both the keys
 * it owns and each key's SHAPE (all plain strings here; `[]` would mean multi
 * and `{}` a date range). It is also exactly what `clearAll()` resets to.
 *
 * DELIBERATELY NOT HERE: the page number. Every filter change below resets it
 * to 1 and the debounced-search effect re-asserts that on mount, so a page
 * number restored from the query string would be overwritten before the first
 * paint -- leaving a URL that says `page=3` over a list showing page 1.
 */
type BrokerPanelFilters = {
  search: string;
  status: string;
  /** U-04: the server ordering field, '' when the list is in its default order. */
  sort: string;
  sort_dir: string;
};

const EMPTY_FILTERS: BrokerPanelFilters = {
  search: '',
  status: '',
  sort: '',
  sort_dir: '',
};

/**
 * The house namespace, as on every page in this sweep.
 *
 * Nothing links to /brokers with a query string today, so there is no
 * unprefixed deep-link seed here (and so no `consumeSeedParams`: there is
 * nothing to consume). The prefix is kept anyway -- `?status=` is the name the
 * API's own filter uses, and an unprefixed filter param on this page would be
 * indistinguishable from a deep link the moment one is added.
 */
const FILTER_PREFIX = 'f_';

/**
 * U-04: the ONLY fields `BrokerViewSet.ordering_fields` accepts -- `name` and
 * `created_at` (backend/apps/brokers/views.py:318). DRF's OrderingFilter
 * silently DROPS a term outside that list and returns the default order
 * (`ordering = ["name"]`), which is exactly how a lying sort header ships
 * unnoticed: it renders, it is clickable, the request goes out, and the list
 * comes back in the order it was already in.
 *
 * Of the seven columns below only Broker (`name`) is in it. Contact, Broker
 * Admin, Licence, Location and Status are not orderable server-side -- `status`
 * in particular is filterable but NOT sortable -- so those headers stay inert
 * text. `created_at` is orderable but this list shows no such column.
 */
const SORTABLE_FIELDS = new Set(['name', 'created_at']);

export function BrokersListPage() {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const [page, setPage] = useState(1);
  const [showAddModal, setShowAddModal] = useState(false);
  const [createdCredentials, setCreatedCredentials] = useState<BrokerAdminCredentials | null>(null);
  // The broker whose status is being changed, and to what. Held together so
  // the confirmation can name both without a second lookup.
  const [statusChange, setStatusChange] = useState<{
    broker: Broker;
    status: BrokerSettableStatus;
  } | null>(null);

  // Q-01: the panel's own filters, mirrored into `?f_*` so they survive a
  // refresh, a bookmark and a shared link. Restored in the hook's LAZY
  // INITIALIZER, so the very first render is already filtered and
  // CompactFilterBar opens its panel over a filtered list rather than painting
  // one frame with the controls hidden and no visible reason for the results.
  const { filters, setFilter, setFilters, clearAll } = useFilterParams<BrokerPanelFilters>({
    defaults: EMPTY_FILTERS,
    prefix: FILTER_PREFIX,
  });

  // Global rule 1 -- search-as-you-type without a request per keystroke:
  // `searchTerm` drives the Input synchronously, `debouncedTerm` (300ms) is
  // what reaches the filters. Seeded from the restored `?f_search=` so a
  // refresh brings the box back filled in, not just the results.
  const { term: searchTerm, debouncedTerm, setTerm: setSearchTerm } = useDebouncedSearch(filters.search);

  // U-04: only a field the server can order by, so a stale or hand-edited
  // `?f_sort=` cannot leave a header drawn as sorted over a list that is not.
  const sortKey = SORTABLE_FIELDS.has(filters.sort) ? filters.sort : '';
  const sortDirection: SortDirection = filters.sort_dir === 'desc' ? 'desc' : 'asc';
  const ordering = sortKey ? `${sortDirection === 'desc' ? '-' : ''}${sortKey}` : undefined;

  // The wire shape. Memoized because it is the react-query key: a fresh object
  // every render would refetch the list on every unrelated re-render.
  const queryFilters = useMemo<BrokerFilters>(
    () => ({
      search: filters.search || undefined,
      status: filters.status || undefined,
      ordering,
    }),
    [filters.search, filters.status, ordering]
  );

  const { data, isLoading, isError, refetch } = useBrokers(page, queryFilters);
  const createBroker = useCreateBroker();
  const setBrokerStatus = useSetBrokerStatus();
  const brokers = useMemo(() => data?.results ?? [], [data]);
  const totalItems = data?.count ?? 0;
  const itemsPerPage = 20;
  const totalPages = Math.ceil(totalItems / itemsPerPage);

  const canAddBroker = user?.role === 'super_admin' || user?.role === 'country_admin';
  // Country Admin no longer manages users at all, so Block / Suspend / Edit
  // have to be reachable from here -- this list is the whole of their broker
  // administration now.
  const canManageBrokers = canAddBroker;

  // Push the debounced term into the filters and reset to the first page.
  //
  // Keyed off a ref of the LAST TERM PUSHED, not off `filters.search`: seeded
  // with the restored value it makes the mount pass a no-op (no second request
  // for a filter that is already applied), and because Clear empties
  // `filters.search` while `debouncedTerm` still holds the old text for another
  // 300ms, comparing against `filters.search` would immediately re-apply the
  // search the user has just cleared.
  const pushedTermRef = useRef(filters.search);
  useEffect(() => {
    if (pushedTermRef.current === debouncedTerm) return;
    pushedTermRef.current = debouncedTerm;
    setFilter('search', debouncedTerm);
    setPage(1);
  }, [debouncedTerm, setFilter]);

  const handleFilterChange = (key: keyof BrokerPanelFilters, value: string) => {
    setFilters({ [key]: value } as Partial<BrokerPanelFilters>);
    setPage(1);
  };

  // U-04: server-side sort. `Table` never reorders the rows it is handed -- it
  // reports the next sort state and this sends it as DRF's `?ordering=`, so the
  // order is the whole filtered book's, not this page of twenty.
  const handleSortChange = (key: string, direction: SortDirection) => {
    setFilters({ sort: key, sort_dir: direction });
    setPage(1);
  };

  // Task 10: the count badges the PANEL's own controls only. The search box is
  // always visible (so it explains itself), and a sort is a view preference,
  // not a filter -- neither badges.
  const activeFilterCount = [filters.status].filter(Boolean).length;

  const clearFilters = () => {
    setSearchTerm('');
    // Empties the `f_` namespace, the sort included, so a shared link
    // reproduces the view the sender was looking at.
    clearAll();
    setPage(1);
  };

  const handleRowClick = (broker: Broker) => {
    navigate(`/brokers/${broker.id}`);
  };

  const handleAddBroker = (data: BrokerCreateRequest, onError: (error: unknown) => void) => {
    createBroker.mutate(data, {
      onSuccess: (response) => {
        setShowAddModal(false);
        // The create response carries one-time admin credentials. Surface
        // them in a "shown once" modal so the admin can copy them now.
        const creds = (response as { admin_credentials?: BrokerAdminCredentials }).admin_credentials;
        if (creds?.password) {
          setCreatedCredentials(creds);
        }
      },
      onError: (error) => {
        onError(error);
      },
    });
  };

  /**
   * U-02: the columns, with a width each.
   *
   * SIZE = CONTENT + CELL PADDING. `Table` pads every `<td>` with `px-6` (48px)
   * and, under `layout="fixed"`, sets `truncate` -- `overflow: hidden`, with no
   * scrollbar, no wrap and no second line. A column declared 12rem therefore
   * offers 144px of content, and anything wider is CLIPPED:
   *
   *   name       14rem px-6   -> 176px
   *   contact    14rem px-6   -> 176px (an email address)
   *   admin      12rem px-6   -> 144px (name + the "Manage user" link)
   *   license    12rem px-6   -> 144px
   *   location   10rem px-6   ->  96px ("Mumbai, Maharashtra" truncates, and
   *                                     carries its own title)
   *   status      9rem px-3   -> 120px ("Pending Approval" as a sm badge:
   *                                     12px text + px-2 -> ~112px)
   *   actions    11.5rem px-3 -> 160px
   *             --------
   *              82.5rem = 1320px, inside the ~1424px a 1440px desktop can show
   *   with no horizontal page scrollbar. Narrower than that the table -- not
   *   the page -- scrolls sideways inside its own card.
   *
   * ACTIONS IS THE COLUMN THAT MATTERS. It holds up to FIVE interactive icon
   * buttons, and a `pending` broker is exactly that case: Edit, Targets,
   * Suspend (status !== 'suspended'), Block (!== 'blocked') and Reactivate
   * (!== 'active') are all drawn at once. Each is `p-1.5` around a `w-4` icon =
   * 6 + 16 + 6 = 28px, with `gap-1` = 4px between: 5 x 28 + 4 x 4 = 156px. At
   * the `px-6` default that needs 204px; overriding the padding to `px-3`
   * (`cn` is tailwind-merge, so a `px-*` in `className` beats the `px-6`) puts
   * it in 11.5rem with 4px to spare. Under `layout="fixed"` a cell that is too
   * narrow for its control does not scroll and does not ellipsise -- the button
   * simply ceases to exist, unclickable and invisible, which is how the
   * Policies page lost its Renew action.
   *
   * Every cell whose render() returns JSX carries its OWN `title`: `Table` sets
   * a tooltip only for string/number content (stringifying arbitrary JSX would
   * show "[object Object]"), so a truncated name, email or licence number would
   * have nothing to hover. The status badge additionally gets `max-w-full` with
   * its label in a `truncate` span -- an `inline-flex` badge is an atomic
   * inline box that a `truncate` ancestor hard-CLIPS with no ellipsis.
   */
  const columns: Column<Broker>[] = [
    {
      key: 'name',
      header: 'Broker',
      width: '14rem',
      // U-04: `name` IS in BrokerViewSet.ordering_fields -- and it is the
      // server's own default order, so this header agrees with what arrives.
      sortable: true,
      render: (broker: Broker) => (
        <div>
          <p className="truncate text-sm font-medium text-gray-900 dark:text-white" title={broker.name}>
            {broker.name}
          </p>
          <p className="truncate text-xs text-gray-500 dark:text-gray-400" title={broker.code}>
            {broker.code}
          </p>
        </div>
      ),
    },
    {
      key: 'contact',
      header: 'Contact',
      width: '14rem',
      // Both lines are read through to the broker admin's own User row by the
      // API, so they never lag behind Country Admin > Users. The old
      // "Admin: <email>" line is gone because it now says the same thing.
      render: (broker: Broker) => (
        <div>
          <p className="truncate text-sm text-gray-900 dark:text-white" title={broker.phone}>
            {broker.phone}
          </p>
          <p className="truncate text-xs text-gray-500 dark:text-gray-400" title={broker.email}>
            {broker.email}
          </p>
        </div>
      ),
    },
    {
      key: 'admin',
      header: 'Broker Admin',
      width: '12rem',
      render: (broker: Broker) => {
        const admin = adminOf(broker);
        if (!admin.admin_id) {
          // Was text-gray-400 on white: ~2.8:1, below the 4.5:1 floor for body
          // text. gray-500 carries the same "nothing here" weight and passes.
          return <p className="truncate text-xs text-gray-500 dark:text-gray-400">No admin user</p>;
        }
        const name = admin.admin_name || broker.admin_email || '';
        return (
          <div>
            <p className="truncate text-sm text-gray-900 dark:text-white" title={name}>
              {name}
            </p>
            {/* The second entry point: a Broker Admin is managed from here as
                well as from the Users section. It lands on the broker's Broker
                Admin tab, where edit / suspend / reset now live -- NOT
                /users/<id>, which a country admin is not routed for (B-05:
                that link ended on Unauthorized). The row itself opens the
                broker, so the link must not bubble. */}
            <Link
              to={`/brokers/${broker.id}?tab=admin`}
              onClick={(e) => e.stopPropagation()}
              className="text-xs text-blue-600 hover:text-blue-800 hover:underline dark:text-blue-400 dark:hover:text-blue-300"
            >
              Manage user
            </Link>
          </div>
        );
      },
    },
    {
      key: 'license',
      header: 'Licence',
      width: '12rem',
      render: (broker: Broker) => {
        const expiryState = licenseExpiryState(broker.license_valid_until);
        const expiry = broker.license_valid_until ? formatDate(broker.license_valid_until) : '-';
        return (
          <div>
            <p
              className="truncate text-sm text-gray-900 dark:text-white"
              title={broker.license_number}
            >
              {broker.license_number}
            </p>
            <p className="truncate text-xs text-gray-500 dark:text-gray-400" title={`Expires ${expiry}`}>
              Exp: {expiry}
            </p>
            {expiryState === 'expired' && (
              <Badge variant="error" size="sm" className="mt-1 max-w-full">
                <span className="truncate">License Expired</span>
              </Badge>
            )}
            {expiryState === 'expiring' && (
              <Badge variant="warning" size="sm" className="mt-1 max-w-full">
                <span className="truncate">Expiring Soon</span>
              </Badge>
            )}
          </div>
        );
      },
    },
    {
      key: 'location',
      header: 'Location',
      width: '10rem',
      render: (broker: Broker) => {
        const location = broker.city && broker.state ? `${broker.city}, ${broker.state}` : '-';
        return (
          <p className="truncate text-sm text-gray-500 dark:text-gray-400" title={location}>
            {location}
          </p>
        );
      },
    },
    {
      key: 'status',
      header: 'Status',
      width: '9rem',
      className: 'px-3',
      render: (broker: Broker) => {
        const label =
          broker.status === 'active'
            ? 'Live'
            : broker.status === 'pending'
              ? 'Pending Approval'
              : broker.status === 'suspended'
                ? 'Suspended'
                : broker.status === 'blocked'
                  ? 'Blocked'
                  : broker.status || 'N/A';
        return (
          <Badge
            variant={
              broker.status === 'active'
                ? 'success'
                : broker.status === 'pending'
                  ? 'warning'
                  : broker.status === 'blocked'
                    ? 'error'
                    : 'default'
            }
            size="sm"
            className="max-w-full"
          >
            <span className="truncate" title={label}>
              {label}
            </span>
          </Badge>
        );
      },
    },
    ...(canManageBrokers
      ? [
          {
            key: 'actions',
            header: 'Actions',
            // 160px of content for five 28px icon buttons and four 4px gaps
            // (156px). See the arithmetic in the block comment above.
            width: '11.5rem',
            className: 'px-3',
            render: (broker: Broker) => (
              // The row navigates on click, so every control in this cell has
              // to stop the bubble or pressing Suspend would open the broker.
              <div
                className="flex items-center justify-end gap-1"
                onClick={(e) => e.stopPropagation()}
              >
                <RowAction
                  title="Edit broker"
                  label={`Edit ${broker.name}`}
                  onClick={() => navigate(`/brokers/${broker.id}?edit=1`)}
                >
                  <PencilSquareIcon className="w-4 h-4" />
                </RowAction>
                {/* The month-wise target grid lives behind the broker's
                    "Performance & Targets" tab. Nothing pointed at it, which is
                    the whole of why it was reported as missing. */}
                <RowAction
                  title="Set monthly targets"
                  label={`Targets for ${broker.name}`}
                  onClick={() => navigate(`/brokers/${broker.id}?tab=performance`)}
                >
                  <ChartBarIcon className="w-4 h-4" />
                </RowAction>
                {broker.status !== 'suspended' && (
                  <RowAction
                    title="Suspend broker"
                    label={`Suspend ${broker.name}`}
                    onClick={() => setStatusChange({ broker, status: 'suspended' })}
                    hoverClassName="hover:text-amber-700 hover:bg-amber-50 dark:hover:text-amber-300 dark:hover:bg-gray-700"
                  >
                    <PauseCircleIcon className="w-4 h-4" />
                  </RowAction>
                )}
                {broker.status !== 'blocked' && (
                  <RowAction
                    title="Block broker"
                    label={`Block ${broker.name}`}
                    onClick={() => setStatusChange({ broker, status: 'blocked' })}
                    hoverClassName="hover:text-red-700 hover:bg-red-50 dark:hover:text-red-300 dark:hover:bg-gray-700"
                  >
                    <NoSymbolIcon className="w-4 h-4" />
                  </RowAction>
                )}
                {broker.status !== 'active' && (
                  <RowAction
                    title="Reactivate broker"
                    label={`Reactivate ${broker.name}`}
                    onClick={() => setStatusChange({ broker, status: 'active' })}
                    hoverClassName="hover:text-green-700 hover:bg-green-50 dark:hover:text-green-300 dark:hover:bg-gray-700"
                  >
                    <PlayCircleIcon className="w-4 h-4" />
                  </RowAction>
                )}
              </div>
            ),
          } as Column<Broker>,
        ]
      : []),
  ];

  return (
    <div>
      <PageHeader
        title="Brokers"
        subtitle={`${totalItems} brokers`}
        breadcrumbs={[{ label: 'Brokers' }]}
        actions={
          <div className="flex items-center gap-3">
            {/* M-02: no "Mapping Requests" button here any more. Agents asking
                to join a brokerage ("Register as POSP") are that brokerage's
                call alone -- Broker Admin > Channels -- and the API no longer
                admits a country admin to them. */}
            {canAddBroker && (
              <Button onClick={() => setShowAddModal(true)} leftIcon={<PlusIcon className="w-5 h-5" />}>
                Add Broker
              </Button>
            )}
          </div>
        }
      />

      <CompactFilterBar
        search={searchTerm}
        onSearchChange={setSearchTerm}
        // Item 1h. This said "or license" and never searched one:
        // `BrokerViewSet.search_fields` is name, code, email and the broker
        // admin's name/email — no `license_number`. A licence number typed here
        // has always returned "no brokers found", which reads as "that licence
        // does not exist". The placeholder names exactly what the server
        // searches.
        searchPlaceholder="Search by name, username, email, or admin..."
        activeFilterCount={activeFilterCount}
        onClearFilters={clearFilters}
      >
        <Select
          id="broker-filter-status"
          label="Status"
          options={statusOptions}
          value={filters.status}
          onChange={(e) => handleFilterChange('status', e.target.value)}
        />
      </CompactFilterBar>

      {/* A REFRESH that failed while the rows it was replacing are still in
          hand -- react-query keeps the previous page's data on a refetch of the
          same query. Without this the toast-free failure is invisible: the rows
          on screen are stale and nothing says so. (A filter CHANGE is a
          different query key and drops to the full error branch below, so this
          can never hide an unapplied filter.) */}
      {isError && brokers.length > 0 && (
        <div
          role="alert"
          className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800 dark:border-red-900 dark:bg-red-900/20 dark:text-red-300"
        >
          <span>Could not refresh this list. These are the results from before your last change.</span>
          <button type="button" className="font-medium underline" onClick={() => void refetch()}>
            Try again
          </button>
        </div>
      )}

      {/* Three states, never two: a failed request, an empty book and a loading
          list are different facts. This page had only the last two, so a 500 --
          or a 403 on a role that may not list brokers -- rendered "No brokers
          found", which reads as "there are no brokers". */}
      {isLoading ? (
        <Table<Broker> data={[]} columns={columns} isLoading rowKey={(broker) => broker.id} />
      ) : isError && brokers.length === 0 ? (
        <Card padding="none">
          <div role="alert" className="px-6 py-12 text-center">
            <p className="text-sm font-medium text-red-700 dark:text-red-400">Could not load brokers.</p>
            <button
              type="button"
              className="mt-3 text-sm text-primary-600 underline hover:no-underline dark:text-primary-400"
              onClick={() => void refetch()}
            >
              Try again
            </button>
          </div>
        </Card>
      ) : brokers.length === 0 ? (
        <Card padding="none">
          <EmptyState />
        </Card>
      ) : (
        <>
          <Table<Broker>
            data={brokers}
            columns={columns}
            rowKey={(broker) => broker.id}
            onRowClick={handleRowClick}
            // U-02: declared widths (no column jump when the filters change)
            // and a header that stays put while the rows scroll under it.
            layout="fixed"
            stickyHeader
            sortKey={sortKey || undefined}
            sortDirection={sortDirection}
            onSortChange={handleSortChange}
          />
          {totalPages > 1 && (
            <Card padding="none" className="mt-4 overflow-hidden">
              <Pagination
                currentPage={page}
                totalPages={totalPages}
                totalItems={totalItems}
                itemsPerPage={itemsPerPage}
                onPageChange={setPage}
              />
            </Card>
          )}
        </>
      )}

      <BrokerStatusModal
        change={statusChange}
        onClose={() => setStatusChange(null)}
        isLoading={setBrokerStatus.isPending}
        onConfirm={(reason) => {
          if (!statusChange) return;
          setBrokerStatus.mutate(
            { id: statusChange.broker.id, status: statusChange.status, reason },
            { onSuccess: () => setStatusChange(null) },
          );
        }}
      />

      {/* Add Broker Modal */}
      <AddBrokerModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onSubmit={handleAddBroker}
        isLoading={createBroker.isPending}
      />

      {/* One-time admin credentials modal (shown only right after creation) */}
      <BrokerCredentialsModal
        credentials={createdCredentials}
        onClose={() => setCreatedCredentials(null)}
      />
    </div>
  );
}

/**
 * One inline row action: an icon button with a real accessible name.
 *
 * Icon-only so five of them fit in one cell without the row growing, with
 * `title` for hover and `aria-label` for everyone else -- an unlabelled icon
 * button is invisible to anyone not looking at it. Its size is load-bearing:
 * `p-1.5` around a `w-4` icon is exactly 28px, which is what the Actions
 * column's width is computed from.
 */
function RowAction({
  title,
  label,
  onClick,
  hoverClassName = 'hover:text-gray-900 hover:bg-gray-100 dark:hover:text-white dark:hover:bg-gray-700',
  children,
}: {
  title: string;
  label: string;
  onClick: () => void;
  hoverClassName?: string;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      title={title}
      aria-label={label}
      onClick={onClick}
      className={`shrink-0 p-1.5 rounded-md text-gray-500 transition-colors dark:text-gray-400 ${hoverClassName}`}
    >
      {children}
    </button>
  );
}

/**
 * Confirm a broker status change, saying what it actually does.
 *
 * The effect line is not decoration: suspending a broker bars their whole team
 * — admin, employees and POSPs — from logging in on every route. An admin who
 * thinks they are changing a label should not find that out afterwards.
 */
function BrokerStatusModal({
  change,
  onClose,
  onConfirm,
  isLoading,
}: {
  change: { broker: Broker; status: BrokerSettableStatus } | null;
  onClose: () => void;
  onConfirm: (reason: string) => void;
  isLoading: boolean;
}) {
  const [reason, setReason] = useState('');

  useEffect(() => {
    setReason('');
  }, [change]);

  if (!change) return null;

  const { broker, status } = change;
  // The verb, not the resulting state: "Suspend Alpha Brokers?" is a question,
  // "Suspended Alpha Brokers?" is a sentence with a typo in it.
  const action = BROKER_STATUS_ACTIONS[status];

  return (
    <Modal isOpen onClose={onClose} title={`${action} ${broker.name}?`} size="md">
      <div className="space-y-4">
        <p className="text-sm text-gray-700 dark:text-gray-300">{BROKER_STATUS_EFFECTS[status]}</p>
        <Input
          label="Reason (optional)"
          name="status_reason"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder={status === 'active' ? 'e.g. License renewed' : 'e.g. License lapsed'}
        />
      </div>
      <ModalFooter>
        <Button variant="outline" type="button" onClick={onClose}>
          Cancel
        </Button>
        <Button
          type="button"
          variant={status === 'active' ? 'primary' : 'danger'}
          isLoading={isLoading}
          onClick={() => onConfirm(reason)}
        >
          {action}
        </Button>
      </ModalFooter>
    </Modal>
  );
}

function BrokerCredentialsModal({
  credentials,
  onClose,
}: {
  credentials: BrokerAdminCredentials | null;
  onClose: () => void;
}) {
  const [copied, setCopied] = useState<string | null>(null);

  const handleCopy = async (label: string, value: string) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(label);
      setTimeout(() => setCopied((c) => (c === label ? null : c)), 2000);
    } catch {
      setCopied(null);
    }
  };

  return (
    <Modal isOpen={!!credentials} onClose={onClose} title="Broker Admin Credentials" size="md">
      {credentials && (
        <div className="space-y-4">
          <div className="rounded-md bg-amber-50 border border-amber-200 p-3 dark:bg-amber-900/20 dark:border-amber-800">
            <p className="text-sm font-medium text-amber-800 dark:text-amber-300">
              These credentials are shown only once.
            </p>
            <p className="mt-1 text-xs text-amber-700 dark:text-amber-400">
              {credentials.message ||
                'Copy and share them securely now — the password will not be shown again.'}
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              Admin Email
            </label>
            <div className="mt-1 flex gap-2">
              <input
                type="text"
                readOnly
                value={credentials.email}
                className="block w-full rounded-md border-gray-300 bg-gray-50 shadow-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white sm:text-sm"
              />
              <Button
                type="button"
                variant="outline"
                onClick={() => handleCopy('email', credentials.email)}
              >
                {copied === 'email' ? 'Copied' : 'Copy'}
              </Button>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              Temporary Password
            </label>
            <div className="mt-1 flex gap-2">
              <input
                type="text"
                readOnly
                value={credentials.password}
                className="block w-full rounded-md border-gray-300 bg-gray-50 font-mono shadow-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white sm:text-sm"
              />
              <Button
                type="button"
                variant="outline"
                onClick={() => handleCopy('password', credentials.password)}
              >
                {copied === 'password' ? 'Copied' : 'Copy'}
              </Button>
            </div>
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              The broker must change this password on first login.
            </p>
          </div>
        </div>
      )}
      <ModalFooter>
        <Button type="button" onClick={onClose}>
          Done
        </Button>
      </ModalFooter>
    </Modal>
  );
}

/**
 * Add Broker — the same record Broker Detail edits, asked for once.
 *
 * Synchronised with `BrokerInformationForm` field for field, and in its
 * vocabulary:
 *   * no "Broker Code" box. `Broker.code` is the "Broker username" the owner
 *     took off the country admin's forms; it still exists and still starts
 *     every policy number, so the SERVER derives one
 *     (`BrokerCreateSerializer.validate` -> `derive_broker_code`).
 *   * "Licence Number" is what this form asks for in its place — a different
 *     column entirely (`license_number`: blank-able, not unique), and the one
 *     the country admin actually has in front of them.
 *   * "Domain Name" writes `website`, the column Broker Detail edits under the
 *     same label. It used to write `custom_domain`, which is not in
 *     `BrokerCreateSerializer.Meta.fields` at all: the value was posted and
 *     dropped in silence. `custom_domain` is the white-label ROUTING host and
 *     keeps its single writer, the broker's own Settings › Domain screen.
 */
function AddBrokerModal({
  isOpen,
  onClose,
  onSubmit,
  isLoading,
}: {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: BrokerCreateRequest, onError: (error: unknown) => void) => void;
  isLoading: boolean;
}) {
  const [formData, setFormData] = useState<BrokerCreateRequest>({ ...EMPTY_NEW_BROKER });
  const [state_name, setStateName] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [phoneErr, setPhoneErr] = useState('');
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [summaryMessages, setSummaryMessages] = useState<string[]>([]);

  // A closed-and-reopened modal must not still hold the last attempt.
  useEffect(() => {
    if (!isOpen) return;
    setFormData({ ...EMPTY_NEW_BROKER });
    setStateName('');
    setPasswordError('');
    setPhoneErr('');
    setFieldErrors({});
    setSummaryMessages([]);
  }, [isOpen]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const phoneMsg = phoneError(formData.phone ?? '');
    if (phoneMsg) {
      setPhoneErr(phoneMsg);
      return;
    }
    setPhoneErr('');
    const password = (formData.admin_password ?? '').trim();
    if (password && password.length < 8) {
      setPasswordError('Password must be at least 8 characters');
      return;
    }
    setPasswordError('');
    setFieldErrors({});
    setSummaryMessages([]);
    // Omit admin_password entirely when blank so the backend auto-generates.
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { admin_password, ...rest } = formData;
    const payload = { ...rest, state: state_name } as BrokerCreateRequest;
    // Empty strings are not values. `license_valid_from`/`_until` in
    // particular are DateFields: DRF rejects "" with a type error rather than
    // reading it as "no date".
    (Object.keys(payload) as (keyof BrokerCreateRequest)[]).forEach((key) => {
      if (payload[key] === '') delete payload[key];
    });
    if (password) payload.admin_password = password;
    onSubmit(payload, (error) => {
      const nextErrors: Record<string, string> = {};
      const messages = applyServerErrors(error, (name, err) => {
        nextErrors[name as string] = err.message;
      });
      setFieldErrors(nextErrors);
      setSummaryMessages(messages);
    });
  };

  const handleChange = (field: keyof BrokerCreateRequest, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (fieldErrors[field]) {
      setFieldErrors((prev) => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Add Broker" size="lg">
      <form onSubmit={handleSubmit}>
        <ErrorSummary messages={summaryMessages} />
        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Input
              label="Broker Name"
              name="name"
              required
              value={formData.name}
              onChange={(e) => handleChange('name', e.target.value)}
              placeholder="Enter broker name"
              error={fieldErrors.name}
            />
            <Input
              label="Domain Name"
              name="website"
              value={formData.website ?? ''}
              onChange={(e) => handleChange('website', e.target.value)}
              placeholder="https://broker.example.com"
              error={fieldErrors.website}
              helperText="The broker's own web address. The white-label portal domain is set later, on Settings › Domain."
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Input
              label="Email"
              name="email"
              type="email"
              required
              value={formData.email || ''}
              onChange={(e) => handleChange('email', e.target.value)}
              placeholder="broker@example.com"
              error={fieldErrors.email}
            />
            <Input
              label="Phone"
              name="phone"
              type="tel"
              required
              value={formData.phone || ''}
              onChange={(e) => {
                handleChange('phone', e.target.value);
                if (phoneErr) setPhoneErr('');
              }}
              placeholder="10-digit mobile number"
              error={phoneErr || fieldErrors.phone}
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Input
              label="Licence Number"
              name="license_number"
              value={formData.license_number ?? ''}
              onChange={(e) => handleChange('license_number', e.target.value)}
              placeholder="e.g. IRDAI/DB/123"
              error={fieldErrors.license_number}
            />
            <Select
              label="Licence Type"
              name="license_type"
              options={LICENSE_TYPE_OPTIONS}
              value={formData.license_type ?? 'direct'}
              onChange={(e) => handleChange('license_type', e.target.value)}
              error={fieldErrors.license_type}
            />
            <Input
              label="Licence Valid From"
              name="license_valid_from"
              type="date"
              max={formData.license_valid_until || undefined}
              value={formData.license_valid_from ?? ''}
              onChange={(e) => handleChange('license_valid_from', e.target.value)}
              error={fieldErrors.license_valid_from}
            />
            <Input
              label="Licence Expiry"
              name="license_valid_until"
              type="date"
              min={formData.license_valid_from || undefined}
              value={formData.license_valid_until ?? ''}
              onChange={(e) => handleChange('license_valid_until', e.target.value)}
              error={fieldErrors.license_valid_until}
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <Input
              label="PAN"
              name="pan_number"
              value={formData.pan_number ?? ''}
              onChange={(e) => handleChange('pan_number', e.target.value.toUpperCase())}
              error={fieldErrors.pan_number}
            />
            <Input
              label="GST Number"
              name="gst_number"
              value={formData.gst_number ?? ''}
              onChange={(e) => handleChange('gst_number', e.target.value.toUpperCase())}
              error={fieldErrors.gst_number}
            />
            <Input
              label="CIN"
              name="cin_number"
              value={formData.cin_number ?? ''}
              onChange={(e) => handleChange('cin_number', e.target.value.toUpperCase())}
              error={fieldErrors.cin_number}
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Input
              label="Address Line 1"
              name="address_line1"
              value={formData.address_line1 || ''}
              onChange={(e) => handleChange('address_line1', e.target.value)}
              placeholder="Address line 1"
              error={fieldErrors.address_line1}
            />
            <Input
              label="Address Line 2"
              name="address_line2"
              value={formData.address_line2 || ''}
              onChange={(e) => handleChange('address_line2', e.target.value)}
              placeholder="Address line 2"
              error={fieldErrors.address_line2}
            />
          </div>

          {/* B6: PIN pre-fills State (and City); State is always a dropdown.
              `state_name` is this modal's OWN piece of state (see above) --
              formData.state is never read here at all -- so it, not
              formData, is what AddressFields is wired to. */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <AddressFields
              pincode={formData.pincode || ''}
              onPincodeChange={(value) => handleChange('pincode', value)}
              pincodeError={fieldErrors.pincode}
              city={formData.city || ''}
              onCityChange={(value) => handleChange('city', value)}
              cityError={fieldErrors.city}
              state={state_name}
              onStateChange={(value) => {
                setStateName(value);
                if (fieldErrors.state) {
                  setFieldErrors((prev) => {
                    const next = { ...prev };
                    delete next.state;
                    return next;
                  });
                }
              }}
              stateError={fieldErrors.state}
            />
          </div>

          {/* Security (optional initial password; matches Add User structure) */}
          <Card>
            <CardHeader title="Security" subtitle="Set the initial password" />
            <div className="max-w-md">
              <Input
                label="Password"
                name="admin_password"
                type="password"
                value={formData.admin_password || ''}
                onChange={(e) => {
                  handleChange('admin_password', e.target.value);
                  if (passwordError) setPasswordError('');
                }}
                placeholder="Leave blank to auto-generate"
                autoComplete="new-password"
                minLength={8}
                error={passwordError || undefined}
              />
              <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                Leave blank to auto-generate. Minimum 8 characters if set. The
                broker must change it on first login.
              </p>
            </div>
          </Card>
        </div>
        <ModalFooter>
          <Button variant="outline" onClick={onClose} type="button">
            Cancel
          </Button>
          <Button type="submit" isLoading={isLoading}>
            Add Broker
          </Button>
        </ModalFooter>
      </form>
    </Modal>
  );
}

function EmptyState() {
  return (
    <div className="text-center py-12">
      <div className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-500">
        <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={1.5}
            d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
          />
        </svg>
      </div>
      <h3 className="mt-4 text-sm font-semibold text-gray-900 dark:text-white">No brokers found</h3>
      <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
        No brokers match your search criteria.
      </p>
    </div>
  );
}
